import { useEffect, useState } from "react";
import { BadgeCheck, Quote } from "lucide-react";
import { api } from "@/lib/api";
import { Stars } from "@/components/Stars";
import { Reveal } from "@/components/Reveal";
import { MaskedLines } from "@/components/MaskedLines";
import { Seo } from "@/components/Seo";
import { useLang } from "@/i18n";

const SUMMARY_VALUES = ["4,9 / 5", "1 800 +", "97 %"];

export default function Testimonials() {
  const { t, lang } = useLang();
  const [reviews, setReviews] = useState([]);

  useEffect(() => {
    api.get("/testimonials").then((r) => setReviews(r.data)).catch(() => {});
  }, []);

  return (
    <main className="mx-auto max-w-7xl px-4 pt-32 pb-24 sm:px-8 lg:px-16">
      <Seo title={`${t("testimonials.eyebrow")} | NordiGuard`} description={t("testimonials.l1")} />

      <header className="pt-8 text-center">
        <p className="text-xs font-semibold uppercase tracking-[0.25em] text-sky-400">{t("testimonials.eyebrow")}</p>
        <h1 className="font-display mt-3 text-4xl font-extrabold tracking-tight text-slate-50 sm:text-5xl lg:text-6xl">
          <MaskedLines lines={[t("testimonials.l1"), t("testimonials.l2")]} />
        </h1>
        <div className="mx-auto mt-10 grid max-w-2xl grid-cols-3 gap-6">
          {SUMMARY_VALUES.map((v, i) => (
            <div key={v} data-testid={`testimonial-stat-${i}`}>
              <div className="font-mono-tech text-2xl font-semibold text-sky-400 sm:text-3xl">{v}</div>
              <div className="mt-1 text-xs text-slate-500">{t("testimonials.stats")[i]}</div>
            </div>
          ))}
        </div>
      </header>

      <div className="mt-16 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {reviews.map((r, i) => (
          <Reveal key={r.id} delay={i * 0.06}>
            <figure data-testid={`testimonial-card-${r.id}`} className="glacial-card flex h-full flex-col p-7">
              <Quote size={22} className="text-sky-500/50" />
              <blockquote className="mt-4 flex-1 text-sm leading-relaxed text-slate-300">
                {lang === "en" ? r.text_en || r.text : r.text}
              </blockquote>
              <figcaption className="mt-6 border-t border-slate-800 pt-5">
                <div className="flex items-center justify-between">
                  <Stars value={r.rating} />
                  <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-emerald-400">
                    <BadgeCheck size={13} /> {t("testimonials.verified")}
                  </span>
                </div>
                <div className="mt-3 font-display text-sm font-bold text-slate-100">{r.name}</div>
                <div className="mt-0.5 text-xs text-slate-500">
                  {r.vehicle} · {r.location} · {t("testimonials.modelPrefix")} {r.product}
                </div>
              </figcaption>
            </figure>
          </Reveal>
        ))}
      </div>

      <Reveal className="mt-16 text-center">
        <p className="text-sm text-slate-400">{t("testimonials.shareQ")}</p>
        <a
          href="/contact"
          data-testid="testimonials-share-cta"
          className="mt-4 inline-block rounded-full border border-sky-400/50 px-7 py-3 text-sm font-semibold text-sky-300 transition-all duration-300 hover:bg-sky-400 hover:text-slate-950"
        >
          {t("testimonials.shareCta")}
        </a>
      </Reveal>
    </main>
  );
}
